import { i18n } from 'boot/i18n'
import DialogDeletePaginated from 'components/dialogs/DialogDeletePaginated.vue'
import Decimal from 'decimal.js-light'
import { cloneDeep } from 'lodash'
import get from 'lodash/get'
import set from 'lodash/set'
import { DateTime } from 'luxon'
import colors from 'quasar/src/utils/colors/colors.js';import copyToClipboard from 'quasar/src/utils/copy-to-clipboard/copy-to-clipboard.js';import Dialog from 'quasar/src/plugins/dialog/Dialog.js';import format from 'quasar/src/utils/format/format.js';import Notify from 'quasar/src/plugins/notify/Notify.js';import scroll from 'quasar/src/utils/scroll/scroll.js';
import { getAPIBaseURL } from 'src/utils/apiBaseUrl'

const { getScrollTarget, setVerticalScrollPosition } = scroll
const { capitalize } = format
const { getPaletteColor } = colors

export { getAPIBaseURL }

export const getPDFUrl = (id, download, token) => {
  return getAPIBaseURL() + '/files/' + id + '?token=' + token + (download ? '&download=1' : '')
}

export const getSettingFileUrl = (id, download, token) => {
  return getAPIBaseURL() + '/setting-files/' + id + '?token=' + token + (download ? '&download=1' : '')
}

/**
 * Contains mapping for translating regex pattern mismatches
 * @type {Record<string, string>}
 */
const patternKeyMap = {
  '^[A-Z0-9]+$': 'UppercaseAlphanumeric',
  '^\\d+$': 'OnlyDigits',
  '^\\d{4}-\\d{2}-\\d{2}$': 'DateYYYYMMDD',
  '^\\+[1-9][0-9]{1,14}$': 'E164PhoneNumber',
}

/**
 * Returns the translation string of know/mapped regex patterns
 * @param {string} pattern
 * @returns {string|null}
 */
export const getPatternKey = (pattern) => patternKeyMap[pattern] ?? null

/**
 * Convert an object schema to empty form data.
 * Optional: Pass the present object data to replace the default values.
 * NOTE: When the present data contains a null value for a nested object, the default data using the schema is returned
 *
 * @param schema - Default object schema
 * @param data - Present data (e.g. returned from the API)
 * @param clone
 */
export const schemaToForm = (schema, data = {}, clone = false) => {
  let obj = {}
  if (data._id) {
    obj._id = data._id
  }

  for (let [key] of Object.entries(schema)) {
    if (schema[key].type === 'object' || schema[key].type === 'custom-object') {
      if (!data[key]) {
        obj[key] = {}
        for (let [childKey, childValue] of Object.entries(schema[key].value)) {
          obj[key][childKey] =
            schema[key].value[childKey].type === 'object' || schema[key].value[childKey].type === 'custom-object'
              ? schemaToForm(schema[key].value[childKey].value, data[key])
              : childValue.value
        }
      } else {
        obj[key] = data[key]

        // Add any object keys that are present in the default schema but not in the data
        for (let [childKey, childValue] of Object.entries(schema[key].value)) {
          if (typeof obj[key][childKey] === 'undefined') {
            obj[key][childKey] = childValue.value
          }
        }
      }
    } else if (schema[key].type === 'array-objects') {
      if (!data[key]) {
        obj[key] = []
      } else {
        obj[key] = data[key]
        if (obj[key].length > 0) {
          // Add any object keys that are present in the default schema but not in the data
          for (const entry of data[key]) {
            for (let [childKey, childValue] of Object.entries(data[key])) {
              if (typeof obj[key][childKey] === 'undefined') {
                entry[childKey] = childValue.value
              }
            }
          }
        }
      }
    } else {
      obj[key] = typeof data[key] !== 'undefined' && data[key] !== null ? data[key] : schema[key].value
    }
  }

  if (!clone) {
    if (data.createdAt) {
      obj.createdAt = data.createdAt
    }
    if (data.updatedAt) {
      obj.updatedAt = data.updatedAt
    }
    if (data.assets) {
      obj.assets = data.assets
    }
  }
  return obj
}

export const generateFormErrorsModel = (res, service) => {
  let errorObject = {}
  const errors = Array.isArray(res.data) ? res.data : []
  const translationPrefix = service === 'users' ? 'Shared.Users' : process.env.FRONTEND_TYPE + '.' + capitalize(service)

  errors.forEach((error) => {
    if (!error.path) {
      return
    }

    let message = []

    const errorPath =
      error.original?.keyword === 'required' && error.original?.params?.missingProperty ? `${error.path}.${error.original.params.missingProperty}` : error.path
    let key = errorPath.split('.').pop()
    const regEx = /\[(\d+)\]/
    if (regEx.test(key)) {
      key = key.split(regEx)
      key = `${key[0]} ${(Number(key[1]) + 1).toString()}`
    }
    const fallbackTranslationKey = translationPrefix + '.' + capitalize(key)
    const nestedTranslationPath = errorPath
      .replace(/^\$\.?/, '')
      .split('.')
      .filter((part) => part && !part.includes('['))
      .map((part) => capitalize(part))
      .join('.')
    const nestedTranslationKey = translationPrefix + '.' + nestedTranslationPath
    const fieldTranslationKey = nestedTranslationPath && i18n.global.te(nestedTranslationKey) ? nestedTranslationKey : fallbackTranslationKey

    let errorKeyword = error.original?.keyword || null

    let errorService = error.service || service

    if (!error.message.includes('anyOf') && !error.message.includes('must be a null')) {
      // Custom errors always have priority
      if (error.original?.params?.customErrorMessage === true) {
        message = [error.message]
      }
      // Otherwise look if we can generate the error based on known validation types from the API
      else if (errorKeyword) {
        let errorParentSchema = error.original?.parentSchema || {}

        // Skip this error to prevent double error messages on invalid object IDs
        if (errorParentSchema?.objectid && errorParentSchema.objectid === true) {
          return
        }

        if (errorKeyword === 'type') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Type.' + errorParentSchema.type)) {
            message = [i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Type.' + errorParentSchema.type, errorParentSchema)]
          } else if (i18n.global.te('Shared.Globals.Errors.Type.' + errorParentSchema.type)) {
            message = [i18n.global.t('Shared.Globals.Errors.Type.' + errorParentSchema.type, errorParentSchema)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Type.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'requiredIf') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIf)[0]
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIf[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIf)[0]
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIf[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'requiredIfNot') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfNot)[0]
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIfNot[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfNot)[0]
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'requiredIfOneOf') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfOneOf)[0]
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIfOneOf[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfOneOf)[0]
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIfOneOf[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'requiredIfOneOfNot') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfOneOfNot)[0]
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIfOneOfNot[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = Object.keys(errorParentSchema.requiredIfOneOfNot)[0]
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey,
                value: String(errorParentSchema.requiredIfOneOfNot[requiredKey]),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'lessThan') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.lessThan
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.lessThan
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'lessThanOrEqual') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.lessThanOrEqual
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.lessThanOrEqual
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'greaterThan') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.greaterThan
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.greaterThan
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'greaterThanOrEqual') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.greaterThanOrEqual
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKey = errorParentSchema.greaterThanOrEqual
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKey: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKey),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'betweenFieldValues') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword)) {
            const requiredKeyFrom = errorParentSchema.betweenFieldValues[0]
            const requiredKeyTo = errorParentSchema.betweenFieldValues[1]
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorKeyword, {
                requiredKeyFrom: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKeyFrom),
                requiredKeyTo: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKeyTo),
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorKeyword)) {
            const requiredKeyFrom = errorParentSchema.betweenFieldValues[0]
            const requiredKeyTo = errorParentSchema.betweenFieldValues[1]
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.' + errorKeyword, {
                requiredKeyFrom: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKeyFrom),
                requiredKeyTo: process.env.FRONTEND_TYPE + '.' + capitalize(service) + '.' + capitalize(requiredKeyTo),
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'notInFuture') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, { key: capitalize(service) + '.' + capitalize(key) })]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'notInPast') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'validateCocNumber') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            const requiredLength = errorParentSchema.validateCocNumber
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                requiredLength,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            const requiredLength = errorParentSchema.validateCocNumber
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                requiredLength,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'validateTaxNumber') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'format') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorParentSchema.format)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorParentSchema.format, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorParentSchema.format)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorParentSchema.format, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'pattern') {
          const patternKey = getPatternKey(errorParentSchema.pattern)

          if (patternKey && errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Patterns.' + patternKey)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Patterns.' + patternKey, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else if (patternKey && i18n.global.te('Shared.Globals.Errors.Patterns.' + patternKey)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Patterns.' + patternKey, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            // Geen mapping of geen specifieke vertaling → val terug op default
            message = [
              i18n.global.t('Shared.Globals.Errors.Patterns.default', {
                key: fieldTranslationKey,
                pattern: errorParentSchema.pattern,
              }),
            ]
          }
        } else if (errorKeyword === 'minimum') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          }
        } else if (errorKeyword === 'minLength') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                minimum: error.original?.params?.limit,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                minimum: error.original?.params?.limit,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                minimum: error.original?.params?.limit,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          }
        } else if (errorKeyword === 'maxLength') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                minimum: error.original?.params?.limit,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          }
        } else if (errorKeyword === 'maximum') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
                ...error.original?.params?.translationProps,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'exclusiveMinimum') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, errorParentSchema)]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, errorParentSchema)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'exclusiveMaximum') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, errorParentSchema)]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, errorParentSchema)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'minItems') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'maxItems') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Formats.' + errorKeyword)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.' + errorKeyword, {
                ...errorParentSchema,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Formats.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'enum') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Type.' + errorKeyword)) {
            message = [i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Type.' + errorKeyword)]
          } else if (i18n.global.te('Shared.Globals.Errors.Type.' + errorKeyword)) {
            message = [i18n.global.t('Shared.Globals.Errors.Type.' + errorKeyword)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Type.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'required') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorParentSchema.type)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.Required.' + errorParentSchema.type, errorParentSchema),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.Required.' + errorParentSchema.type)) {
            message = [i18n.global.t('Shared.Globals.Errors.Required.' + errorParentSchema.type, errorParentSchema)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'colorHex') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.ColorHex.' + errorParentSchema.errorCode)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.ColorHex.' + errorParentSchema.errorCode, errorParentSchema),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.ColorHex.' + errorParentSchema.errorCode)) {
            message = [i18n.global.t('Shared.Globals.Errors.ColorHex.' + errorParentSchema.errorCode, errorParentSchema)]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.Required.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        } else if (errorKeyword === 'errorCode' || errorKeyword === 'custom-error') {
          if (errorService && i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.ErrorCode.' + errorParentSchema.errorCode)) {
            message = [
              i18n.global.t(process.env.FRONTEND_TYPE + '.' + capitalize(errorService) + '.Errors.ErrorCode.' + errorParentSchema.errorCode, {
                ...errorParentSchema,
                ...error.original?.params?.translationProps,
                key: fieldTranslationKey,
              }),
            ]
          } else if (i18n.global.te('Shared.Globals.Errors.ErrorCode.' + errorParentSchema.errorCode)) {
            message = [
              i18n.global.t('Shared.Globals.Errors.ErrorCode.' + errorParentSchema.errorCode, {
                ...errorParentSchema,
                ...error.original?.params?.translationProps,
                key: fieldTranslationKey,
              }),
            ]
          } else {
            message = [
              i18n.global.t('Shared.Globals.Errors.ErrorCode.default', {
                key: fieldTranslationKey,
              }),
            ]
          }
        }
        // Otherwise fallback to "ugly error messages" and filter out a few that are always to be ignored
        else {
          message = [error.message]
        }
      }
      // Otherwise fallback to "ugly error messages" and filter out a few that are always to be ignored
      else {
        message = [error.message]
      }
    }

    if (message.length > 0) {
      let existingValue = get(errorObject, errorPath + '.$errors')
      if (!existingValue) {
        set(errorObject, errorPath + '.$errors', message)
      } else {
        set(errorObject, errorPath + '.$errors', [...message, ...existingValue])
      }
    }
  })

  return errorObject['$'] || {}
}

export const getErrorMessage = (itemKey, props) => {
  if (props && props['$errors']) {
    const errorMessages = []

    props['$errors'].forEach((message) => {
      if (!errorMessages.includes(message)) {
        errorMessages.push(message)
      }
    })
    return errorMessages.join(' ')
  } else if (props && props[itemKey]) {
    const errorMessages = []
    props[itemKey].forEach((message) => {
      if (!errorMessages.includes(message)) {
        errorMessages.push(message)
      }
    })
    return errorMessages.join(' ')
  } else {
    return ''
  }
}

export const getFieldType = (type) => {
  switch (type) {
    case 'string':
      return 'text'
    case 'textarea':
      return 'textarea'
    case 'number':
      return 'number'
    case 'phone':
      return 'tel'
    default:
      return 'text'
  }
}

export const getOptions = (
  key,
  options,
  itemsService,
  config = {
    field: null,
    valueKey: null,
    labelKey: null,
    translateKeyBase: null,
    translateKey: null,
    avatar: false,
    avatarValuePrefix: null,
    avatarValue: null,
  },
  preserveOriginalValues = false,
  translateOptions = true,
  translationOptionsPrefix = null,
) => {
  if (!Array.isArray(options)) {
    return []
  }

  let translationPath = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService)
  if (itemsService === 'users') {
    translationPath = 'Shared.Users'
  }

  let arr = []
  for (const option of options) {
    let stringOption = ''
    let message = ''
    stringOption = typeof option === 'boolean' ? (option ? 'yes' : 'no') : option
    if (typeof option === 'object' && Object.keys(config).every((option) => config[option])) {
      message = translateOptions ? config.translateKeyBase + '.' + capitalize(stringOption[config.translateKey]) : stringOption[config.translateKey]
      let optionObj = {
        value: option[config.valueKey],
        label: i18n.global.t(message),
      }
      if (config.avatar) {
        optionObj.avatar = (config.avatarValuePrefix ? config.avatarValuePrefix : '') + option[config.avatarValue].toLowerCase()
      }
      if (preserveOriginalValues) {
        optionObj.originalValue = option
      }
      arr.push(optionObj)
    } else if (typeof option === 'object' && !Object.keys(config).every((option) => config[option])) {
      arr.push(option)
    } else {
      message =
        translateOptions && key && itemsService && typeof option !== 'boolean'
          ? translationOptionsPrefix
            ? translationOptionsPrefix + '.' + capitalize(stringOption)
            : translationPath + '.' + capitalize(key) + 'Options.' + capitalize(stringOption)
          : stringOption

      arr.push({
        value: option,
        label: translateOptions ? i18n.global.t(message) : String(message),
      })
    }
  }
  return arr
}

export const filterTable = (rows, terms, filters, excludedKeys = []) => {
  return rows.filter((row) => {
    let validFields = []
    for (let item in filters.value) {
      let checkItem = null
      if (!item.includes('.')) {
        checkItem = row[item]
      } else {
        checkItem = get(row, item)
      }

      if (
        filters.value[item]?.some((option) => {
          if (Array.isArray(checkItem) && checkItem.length > 0) {
            if (checkItem.every((i) => typeof i === 'string')) {
              return checkItem.includes(option)
            } else {
              let found = false
              for (const entry of checkItem) {
                if (typeof entry === 'object') {
                  found = Object.keys(entry).some((k) => entry[k] === option)
                } else {
                  if (entry === option) {
                    found = true
                  }
                }
                if (found) {
                  break
                }
              }
              return found
            }
          } else {
            return (
              typeof checkItem === 'undefined' ||
              (checkItem === null && checkItem === option) ||
              (checkItem !== null && option === '__NOTNULL__') ||
              (checkItem !== null && typeof checkItem === 'boolean' && checkItem === option) ||
              (checkItem !== null && typeof checkItem === 'object' && (option === true || option === null)) ||
              (checkItem === '' && typeof checkItem === 'string' && option === null) ||
              (checkItem !== null && typeof checkItem !== 'boolean' && typeof checkItem !== 'object' && checkItem === option)
            )
          }
        })
      ) {
        validFields.push(item)
      } else {
        return false
      }
    }
    return validFields.length === Object.keys(filters.value).length && searchValueInRow(row, terms.search, excludedKeys)
  })
}

export const validateField = (key, schema = {}, val) => {
  let rulesResults = {
    status: true,
    message: '',
  }
  if (val) {
    schema.rules?.forEach((rule) => {
      switch (rule.type) {
        case 'regex':
          const re = new RegExp(rule.value, 'g')
          let regexResult = re.test(val)
          rulesResults.status = regexResult
          if (!regexResult) {
            rulesResults.message = 'Globals.Invalid'
          }
          break
        case 'url':
          let givenUrl
          try {
            givenUrl = new URL(val)
          } catch (e) {
            rulesResults.status = false
          }
          const isValid = givenUrl?.protocol === 'https:'
          if (!isValid) {
            rulesResults.message = 'Globals.Invalid'
          }
          rulesResults.status = isValid
          break
      }
    })
  } else {
    if (schema.required) {
      rulesResults.status = false
      rulesResults.message = 'Globals.Mandatory'
    } else {
      rulesResults.status = true
    }
  }
  return rulesResults
}

export const searchValueInRow = (row, searchValue = '', excludedKeys = []) => {
  const lowerSearch = searchValue ? searchValue.toLowerCase() : ''
  const searchableArray = Array.isArray(row) ? row : Object.keys(row)

  return searchableArray.some((key) => {
    let found = false
    if (!excludedKeys.includes(key)) {
      if (row[key] && typeof row[key] === 'object') {
        found = searchValueInRow(row[key], searchValue, excludedKeys)
      } else if (row[key] && typeof row[key] === 'string') {
        found = row[key].toLowerCase().includes(lowerSearch)
      } else if (row[key] && typeof row[key] === 'number') {
        found = row[key].toString().includes(lowerSearch)
      } else if (typeof key === 'string') {
        found = key.toLowerCase().includes(lowerSearch)
      }
    }
    return found
  })
}

// Preload tabs when using split forms for form validation
export const tabPreLoader = async (
  options = {
    tabRef: null,
    tabNames: [],
    startTab: '',
    loadInnerTabs: false,
    innerTabsSettings: [],
  },
) => {
  await options.tabNames
    .reduce(async (promiseChain, item) => {
      return promiseChain.then(
        () =>
          new Promise(async (resolve) => {
            options.tabRef?.value?.goTo(item)
            if (options.loadInnerTabs) {
              for (const innerTabSetting of options.innerTabsSettings) {
                if (item === innerTabSetting.innerTabParent) {
                  await tabPreLoader({
                    tabRef: innerTabSetting.innerTabRef,
                    tabNames: innerTabSetting.innerTabNames,
                    startTab: innerTabSetting.innerStartTab,
                  })
                }
              }
            }
            resolve()
          }),
      )
    }, Promise.resolve())
    .then(() => {
      options.tabRef?.value?.goTo(options.startTab) // when all is finished, goto the preferred start tab
    })
}

export const onError = (payload, itemsService) => {
  let translateString = ''
  if (
    payload.errors?.errorCode &&
    i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.Errors.ErrorCode.' + payload.errors.errorCode)
  ) {
    translateString = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.Errors.ErrorCode.' + payload.errors.errorCode
  } else if (payload.errors?.errorCode && i18n.global.te('Shared.Globals.Errors.ErrorCode.' + payload.errors.errorCode)) {
    translateString = 'Shared.Globals.Errors.ErrorCode.' + payload.errors.errorCode
  } else if (
    payload.item &&
    i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail ' + payload.item)
  ) {
    translateString = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail ' + payload.item
  } else if (i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail')) {
    translateString = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail'
  } else if (i18n.global.te(process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.Errors.default')) {
    translateString = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService) + '.Errors.default'
  } else {
    translateString = 'Shared.Globals.Errors.Item fail'
  }

  Notify.create({
    type: 'negative',
    message: i18n.global.t(translateString, {
      msg: payload.errors?.showErrorMessage === true ? capitalize(payload.message) : null,
    }),
    progress: true,
    html: true,
  })
}

export const onSuccess = (payload, itemsService, type = '', index = 1) => {
  let translationPath = process.env.FRONTEND_TYPE + '.' + capitalize(itemsService)
  if (itemsService === 'users') {
    translationPath = 'Shared.Users'
  }
  Notify.create({
    type: 'positive',
    message: i18n.global.t(
      translationPath + '.' + capitalize(type) + ' item success',
      {
        item: payload.fullName ? payload.fullName : payload.name,
      },
      index,
    ),
    progress: true,
    html: true,
  })
}

export const openDialogForm = (
  options = {
    type: 'new',
    icon: null,
    subtitle: null,
    component: null,
    itemsService: null,
    itemName: null,
    props: null,
    onOk: null,
    onCancel: null,
    onError: null,
    onSuccess: null,
    onSuccessData: null,
    onDismiss: null,
    customProps: null,
    deleteOnService: null,
  },
) => {
  if (options.type === 'delete' && !options.component) {
    options.component = DialogDeletePaginated
  }

  const { validationErrors } = storeToRefs(options.store)

  let thisStore = null

  if (options.store) {
    thisStore = options.store()
  }

  Dialog.create({
    component: options.component,
    parent: this,
    componentProps: {
      type: options.type,
      dialogIcon: options.icon,
      dialogSubtitle: options.subtitle,
      propItem: options.props,
      deleteOnService: options.deleteOnService,
      itemName: options.itemName ? options.itemName : undefined,
      itemsService: options.itemsService,
      onError: options.onError ? options.onError : onError,
      onSuccess: options.onSuccess ? options.onSuccess : onSuccess,
      onSuccessData: options.onSuccessData,
      customProps: options.customProps ? options.customProps : undefined,
    },
  })
    .onOk(async (payload) => {
      if (thisStore && !options.customProps?.preventItemClear) {
        // thisStore.$state.item = null
      }
      if (options.onOk) {
        options.onOk(payload)
      }
    })
    .onCancel((payload) => {
      if (thisStore && !options.customProps?.preventItemClear) {
        // thisStore.$state.item = null
      }
      if (options.onCancel) {
        options.onCancel(payload)
      }
    })
    .onDismiss((payload) => {
      if (thisStore && !options.customProps?.preventItemClear) {
        // thisStore.$state.item = null
      }
      if (options.onDismiss) {
        options.onDismiss(payload)
      } else {
        if (validationErrors) {
          validationErrors.value = {}
        }
      }
    })
}

export const checkSubData = (data) => {
  return Object.values(data ?? {}).some((value) => {
    return value !== null && typeof value === 'object'
  })
}

export const camelToFlat = (string) => {
  const camelCase = string.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ')

  let flat = ''

  camelCase.forEach((word) => {
    flat = flat + word.charAt(0).toUpperCase() + word.slice(1) + ' '
  })
  return flat.trim()
}

/**
 * Normalize the field configuration into a consistent format:
 *   - string → { path: 'fieldName', direction: 1 }
 *   - '-field' → { path: 'field', direction: -1 }
 *   - { field: 'name', desc: true } → { path: 'name', direction: -1 }
 */
const normalizeSortFields = (fields) => {
  if (typeof fields === 'string') fields = [fields]

  return fields.map((field) => {
    if (typeof field === 'string') {
      const isDescending = field.startsWith('-')
      const path = field.replace(/^-/, '')
      return { path, direction: isDescending ? -1 : 1 }
    }

    const path = field.field ?? field.path
    const direction = typeof field.direction === 'number' ? Math.sign(field.direction) || 1 : field.desc ? -1 : 1

    return { path, direction }
  })
}

/**
 * Assign a rank for value types so different types can be compared consistently.
 */
const getTypeRank = (value) => {
  if (value === null || value === undefined) return 9
  if (typeof value === 'boolean') return 0
  if (typeof value === 'number') return 1
  if (value instanceof Date) return 2
  if (typeof value === 'string') return 3
  return 8 // objects or other types
}

/**
 * Convert values into a comparable form depending on type.
 */
const normalizeValue = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'string') return value.toLocaleUpperCase()
  return value
}

/**
 * Sort an array of objects by one or multiple fields.
 * Supports nested paths, case-insensitive strings, and mixed data types.
 *
 * @param {Array} data - The array to be sorted
 * @param {string|string[]|object[]} fields - Field(s) to sort by
 * @param {boolean} ascending - Global sort direction (default: true)
 * @returns {Array} - Sorted copy of the input array
 */
export const sortObjectBy = (data, fields, ascending = true) => {
  if (!Array.isArray(data)) return []

  const items = [...data] // avoid mutating the original array
  const sortFields = normalizeSortFields(fields)
  const globalDirection = ascending ? 1 : -1

  return items.sort((a, b) => {
    for (const { path, direction } of sortFields) {
      const aValue = get(a, path)
      const bValue = get(b, path)

      // Skip if values are equal or both undefined/null
      if (aValue === bValue) continue

      // Rank type differences first (e.g., boolean vs string)
      const aRank = getTypeRank(aValue)
      const bRank = getTypeRank(bValue)
      if (aRank !== bRank) return (aRank - bRank) * direction * globalDirection

      const aKey = normalizeValue(aValue)
      const bKey = normalizeValue(bValue)

      // String comparison (case-insensitive, numeric aware)
      if (typeof aKey === 'string' && typeof bKey === 'string') {
        const result = aKey.localeCompare(bKey, undefined, {
          sensitivity: 'base',
          numeric: true,
        })
        if (result !== 0) return result * direction * globalDirection
      } else {
        // General comparison for numbers, booleans, dates, etc.
        if (aKey < bKey) return -1 * direction * globalDirection
        if (aKey > bKey) return 1 * direction * globalDirection
      }
    }

    return 0 // all compared fields are equal
  })
}

/**
 * Convert number to localized value
 * @param {string} lang                           - ISO language code to use for localization (e.g. en-US)
 * @param {number} number                         - Number to convert
 * @param {boolean} [showCurrencySymbol=false]    - Convert number to currency?
 * @param {string} [type='currency']              - Style for formatting. See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options|MDN Webdocs} for options. NOTE: Is omitted when showCurrencySymbol is set to true
 * @param {object} [options={}]                   - Additional options for formatting. See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options|MDN Webdocs} for options. NOTE: Is omitted when showCurrencySymbol is set to true
 * @returns {string}                              - Formatted localized string
 */
export const numberFormat = (lang, number, showCurrencySymbol = false, type = 'currency', options = {}) => {
  if (typeof options.minimumFractionDigits === 'undefined') {
    let minDigits = 2
    if (type === 'percentage') {
      minDigits = 0
    }
    options = {
      ...options,
      minimumFractionDigits: minDigits,
    }
  }

  if (typeof options.maximumFractionDigits === 'undefined') {
    options = {
      ...options,
      maximumFractionDigits: 2,
    }
  }

  if (showCurrencySymbol) {
    options.style = 'currency'
    options.currency = 'EUR'
  }

  return new Intl.NumberFormat(lang, options).format(number)
}

export const validateBase64 = (base64) => {
  const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return base64regex.test(base64)
}

export const base64PDFToBlobUrl = (base64) => {
  const binStr = atob(base64)
  const len = binStr.length
  const arr = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i)
  }
  const blob = new Blob([arr], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export const isOverdue = (date) => {
  return DateTime.now() > DateTime.fromISO(date)
}

export const itemIsObjectNotArray = (data) => {
  return typeof data === 'object' && !Array.isArray(data)
}

/**
 * Adds all calculations together for (selected) customers
 *
 * @param {Object} source - Object with data to add
 * @param {Object} destination - Object to which the source should be added
 * @returns {Object} - Objects given as destination param, but with source data added to it
 */
export const addToTarget = (source, destination) => {
  let obj = { ...destination }

  for (const item of Object.keys(source)) {
    if (item.toLowerCase() === 'error') {
      continue
    }

    if (itemIsObjectNotArray(source[item])) {
      if (typeof obj[item] === 'undefined') {
        obj[item] = {}
      }
      obj[item] = { ...obj[item], ...addToTarget(source[item], obj[item]) }
    } else {
      if (typeof obj[item] === 'undefined') {
        obj[item] = Array.isArray(source[item]) ? [] : new Decimal(0)
      }
      if (Array.isArray(source[item])) {
        obj[item] = obj[item].concat(source[item])
      } else {
        obj[item] = new Decimal(obj[item]).plus(new Decimal(source[item]))
      }
    }
  }

  return obj
}

export const createChartDataSet = (
  data = {},
  fields = [],
  parentKey = null,
  alternateLabel = null,
  alternateLabelType = 'string',
  stripFromLabel = '',
  sortLegend = true,
  customColors = [],
) => {
  let labels = []
  let chartData = []
  let colorCount = 0

  let itemData = {
    data: [],
    backgroundColor: [],
  }

  if (itemIsObjectNotArray(data)) {
    const colors = randomRGBAArray(Object.keys(data).length)

    if (customColors && customColors.length > 0) {
      const itemsToReplace = Math.min(customColors.length, colors.length)

      for (let i = 0; i < itemsToReplace; i++) {
        colors[i] = hexToRgb(customColors[i])
      }
    }

    for (let key of Object.keys(data)) {
      const strippedKey = alternateLabel
        ? alternateLabelType === 'string'
          ? camelToFlat(alternateLabel.replaceAll(stripFromLabel, ''))
          : data[alternateLabel].replaceAll(stripFromLabel, '')
        : camelToFlat(key.replaceAll(stripFromLabel, ''))

      if (fields.length === 0 || fields.includes(key)) {
        labels.push(strippedKey)

        if (parentKey) {
          itemData.label = camelToFlat(parentKey)
        }

        itemData.data.push({ key: strippedKey, value: data[key] })
        itemData.backgroundColor.push(colors[colorCount])

        colorCount++
      }
    }
  } else {
    for (const item of data) {
      const colors = randomRGBAArray(data.length)

      for (let key of Object.keys(item)) {
        const strippedKey = alternateLabel
          ? alternateLabelType === 'string'
            ? camelToFlat(alternateLabel.replaceAll(stripFromLabel, ''))
            : item[alternateLabel].replaceAll(stripFromLabel, '')
          : camelToFlat(key.replaceAll(stripFromLabel, ''))

        if (fields.length === 0 || fields.includes(key)) {
          labels.push(strippedKey)

          if (parentKey) {
            itemData.label = camelToFlat(parentKey)
          }

          itemData.data.push({ key: strippedKey, value: item[key] })
          itemData.backgroundColor.push(colors[colorCount])
        }
      }

      colorCount++
    }
  }

  chartData.push(itemData)

  if (sortLegend) {
    chartData.forEach((set) => {
      set.data = sortObjectBy(set.data, 'key')
    })
  }

  return {
    labels: sortLegend ? labels.sort() : labels,
    datasets: chartData,
  }
}

/**
 * Adds all calculations together for (selected) customers to add into a line chart
 * @param {Object} data - Object with data to add
 * @param {string} keyPath - String with JSPath
 * @param {Array} excludedKeys - Array with keys that should be excluded from the chart
 * @param {boolean} sortLegend - Should the legend be sorted
 * @param {boolean} [round=false] - Should the values be rounded
 * @param {number} [digits=0] - If the value should be rounded, how many decimals
 * @param {boolean} [stepped=false] - Show chart as a stepped line chart
 * @param {Array} customColors
 * @returns {Object} - Data object with datasets and labels
 */
export const createChartDataSetHistory = (
  data,
  keyPath,
  excludedKeys = [],
  sortLegend = true,
  round = false,
  digits = 0,
  stepped = false,
  customColors = [],
) => {
  const chartDataMap = new Map()
  const dateRange = Object.keys(data)

  const labelArray = []

  for (const dateData of Object.values(data)) {
    const getPath = get(dateData, keyPath)

    if (!getPath) {
      continue
    }

    for (const key of Object.keys(getPath)) {
      if (excludedKeys.includes(key)) {
        continue
      }
      if (!labelArray.includes(camelToFlat(key))) {
        labelArray.push(camelToFlat(key))
      }
    }
  }

  const colors = randomRGBAArray(labelArray.length)

  if (customColors && customColors.length > 0) {
    const itemsToReplace = Math.min(customColors.length, colors.length)

    for (let i = 0; i < itemsToReplace; i++) {
      colors[i] = hexToRgb(customColors[i])
    }
  }

  let colorCount = 0
  for (const date of dateRange) {
    const requestedChartData = get(data[date], keyPath)

    if (!requestedChartData) {
      continue
    }

    const chartDataEntries = Object.entries(requestedChartData)
    const labelArrayLastRun = []

    for (const [key, value] of chartDataEntries) {
      if (excludedKeys.includes(key)) {
        continue
      }
      const formattedKey = camelToFlat(key)
      let dataset = chartDataMap.get(formattedKey)

      if (!dataset) {
        dataset = {
          label: formattedKey,
          data: [],
          backgroundColor: colors[colorCount],
          borderColor: colors[colorCount],
          stepped,
        }

        colorCount++
        chartDataMap.set(formattedKey, dataset)
      }

      if (!labelArrayLastRun.includes(formattedKey)) {
        labelArrayLastRun.push(formattedKey)
      }

      dataset.data.push({
        x: date,
        y: round ? Number(value).toFixed(digits) : value,
      })
    }

    const missingKeys = labelArray.filter((label) => !labelArrayLastRun.includes(label))

    for (const missingKey of missingKeys) {
      const formattedMissingKeys = camelToFlat(missingKey)
      let dataset = chartDataMap.get(formattedMissingKeys)
      if (!dataset) {
        dataset = {
          label: formattedMissingKeys,
          data: [],
          backgroundColor: colors[colorCount],
          borderColor: colors[colorCount],
          stepped,
        }

        colorCount++
        chartDataMap.set(formattedMissingKeys, dataset)
      }

      dataset.data.push({ x: date, y: round ? Number(0).toFixed(digits) : 0 })
    }
  }

  return {
    datasets: sortLegend ? sortObjectBy(Array.from(chartDataMap.values()), 'label') : Array.from(chartDataMap.values()),
  }
}

/**
 * Converts a hex color string to an RGB string.
 * Supports both full (e.g., "#RRGGBB") and short (e.g., "#RGB") formats.
 *
 * @param {string} hex - The hex color string, for example "#FF5733" or "#F53".
 * @returns {string} The resulting RGB color string, e.g., "rgb(255, 87, 51)".
 * @throws {Error} If the hex format is invalid.
 */
export const hexToRgb = (hex) => {
  // Remove the '#' character if present.
  hex = hex.replace(/^#/, '')

  // If the string is 3 characters long, expand each character to 2.
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  // Check if the string now has exactly 6 characters.
  if (hex.length !== 6) {
    throw new Error('Invalid hex color.')
  }

  // Parse the three color components and form the RGB string.
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  return `rgb(${r}, ${g}, ${b})`
}

export const hsvToRgb = (h, s, v) => {
  const h_i = Math.floor(h * 6)
  const f = h * 6 - h_i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)
  let r, g, b

  switch (h_i) {
    case 0:
      r = v
      g = t
      b = p
      break
    case 1:
      r = q
      g = v
      b = p
      break
    case 2:
      r = p
      g = v
      b = t
      break
    case 3:
      r = p
      g = q
      b = v
      break
    case 4:
      r = t
      g = p
      b = v
      break
    case 5:
      r = v
      g = p
      b = q
      break
  }

  return [Math.floor(r * 255), Math.floor(g * 255), Math.floor(b * 255)]
}

export const randomRGBAArray = (count = 1) => {
  const colors = []
  const goldenRatioConjugate = 0.618033988749895

  const baseHues = [
    60 / 360, // yellow
    120 / 360, // green
    240 / 360, // blue
    0 / 360, // red
    180 / 360, // cyan
    300 / 360, // magenta
    30 / 360, // orange
    275 / 360, // indigo
    90 / 360, // lime
  ]

  const hues = []
  for (let i = 0; i < Math.min(count, baseHues.length); i += 1) {
    hues.push(baseHues[i])
  }
  let h = baseHues[baseHues.length - 1] || 0.08
  for (let i = hues.length; i < count; i += 1) {
    h = (h + goldenRatioConjugate) % 1
    hues.push(h)
  }

  const saturation = 0.75
  const value = 0.9

  for (let i = 0; i < count; i += 1) {
    const rgb = hsvToRgb(hues[i], saturation, value)
    colors.push(rgb)
  }

  const mappedColors = colors.map((color) => `rgb(${color.join(',')})`)

  if (mappedColors.length > 0) {
    const primaryColor = getPaletteColor?.('primary')

    if (typeof primaryColor === 'string' && primaryColor.trim().length > 0) {
      const normalizedPrimaryColor = primaryColor.trim()
      mappedColors[0] = normalizedPrimaryColor.startsWith('#') ? hexToRgb(normalizedPrimaryColor) : normalizedPrimaryColor
    }
  }

  return mappedColors
}

export const copyText = (text) => {
  copyToClipboard(text).catch((e) => {
    Notify.create({
      type: 'negative',
      message: i18n.global.t('Shared.Globals.Copy fail', { error: e }),
    })
  })

  Notify.create({
    type: 'positive',
    icon: 'sym_o_content_copy',
    message: i18n.global.t('Shared.Globals.Copy success'),
  })
}

// takes an element object
export const scrollToElement = (selector, duration = 1000) => {
  const element = document.querySelector(selector)
  window.testElement = element
  if (element) {
    const target = getScrollTarget(element)
    const offset = element.offsetTop
    setVerticalScrollPosition(target, offset, duration)
  }
}

export const areArraysEqualUnordered = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false
  }

  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false
    }
  }

  return true
}

/**
 * Compare 2 object for equality
 * @param object1
 * @param object2
 * @returns {boolean}
 */
export const areObjectsEqual = (object1, object2) => {
  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    const val1 = object1[key]
    const val2 = object2[key]
    const areObjects = isObject(val1) && isObject(val2)
    if ((areObjects && !areObjectsEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false
    }
  }

  return true
}

/**
 * Determines if data is an object
 * @param object
 * @returns {boolean}
 */
export const isObject = (object) => {
  return object != null && typeof object === 'object'
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const updateArray = (array, item, remove = false, resort = null) => {
  if (!item) {
    return
  }

  const foundIndex = array.findIndex((arrayItem) => arrayItem._id === item._id)
  if (foundIndex > -1) {
    if (remove) {
      array.splice(foundIndex, 1)
    } else {
      array.splice(foundIndex, 1, item)
    }
  } else {
    array.push(item)
  }
}

/**
 * Moves an item in an Array from an old index to a new index
 * @param arrayToReorder      - Array to move item in
 * @param oldIndex              - Old index
 * @param newIndex              - New index
 * @returns {Array}           - Reordered array
 */
export const reorderArrayBasedOnIndexes = (arrayToReorder, oldIndex, newIndex) => {
  const itemToMove = arrayToReorder.splice(oldIndex, 1)[0]
  arrayToReorder.splice(newIndex, 0, itemToMove)
  return arrayToReorder
}

/**
 * Converts number to Object with hours and minutes
 * @param number
 * @returns {{hours: number, minutes: number}|{hours: (number|number), minutes: number}}
 */
export const hoursToHuman = (number) => {
  if (!number || isNaN(number)) {
    return { hours: 0, minutes: 0 }
  }

  let hours = Math.floor(number)
  let minutes = Math.round((number - hours) * 60)

  // Round minutes to the nearest 5-minute interval
  minutes = Math.ceil(minutes / 5) * 5

  // Adjust hours if minutes exceeds 59
  if (minutes >= 60) {
    hours++
    minutes -= 60
  }

  return { hours, minutes }
}

/**
 * Calculates the quota based on the provided key and data, with an optional override for the quota limit.
 *
 * @param {string} key - The key to access the specific quota data.
 * @param {string} usageKey - String to optionally override key name with usage info.
 * @param {string} limitKey - String to optionally override key name with limit info.
 * @param {boolean} useIndex - Boolean to determine if data should set using the index instead of the keyName
 * @param {number} index - Index of data when useIndex is set to true.
 * @param {Object} data - The data object containing quota information.
 * @param {number|null} [overrideQuota=null] - An optional override value for the quota limit. Defaults to null.
 * @returns {Object} An object containing the calculated limit, size, and color.
 *
 * @property {number|null} limit - The limit for the specified quota.
 * @property {number|null} size - The calculated size ratio based on usage and limit, rounded to two decimal places.
 * @property {string} color - The color indicator based on the calculated size ratio. ('positive', 'warning', 'negative', or 'black')
 */
export const getQuota = (key, data, overrideQuota = null, usageKey = 'usage', limitKey = 'limit', useIndex = false, index = null) => {
  if (!data[useIndex ? index : key]) {
    return {
      limit: null,
      size: null,
      color: 'black',
    }
  }

  if (overrideQuota) {
    data[useIndex ? index : key][limitKey] = overrideQuota
  }

  const result = {
    limit: data[useIndex ? index : key][limitKey],
  }

  if (data[useIndex ? index : key][limitKey] === 0) {
    result.size = 0
  } else {
    result.size = Number(parseFloat(data[useIndex ? index : key][usageKey] / data[useIndex ? index : key][limitKey]).toFixed(2))
  }

  if (result.size < 0.8) {
    result.color = 'positive'
  } else if (result.size < 0.95) {
    result.color = 'warning'
  } else {
    if (result.limit === 0) {
      result.color = 'positive'
    } else {
      result.color = 'negative'
    }
  }

  return result
}

export const createFormInstance = (onlyFields = null, schema) => {
  if (Array.isArray(onlyFields)) {
    const clonedSchema = cloneDeep(toRaw(schema))
    const filteredSchema = {}
    for (const field of onlyFields) {
      if (clonedSchema.hasOwnProperty(field)) {
        filteredSchema[field] = clonedSchema[field]
      }
    }
    return filteredSchema
  } else {
    return cloneDeep(toRaw(schema))
  }
}

/**
 * Retrieves the applicable discount from the subscription information.
 * @param {Object} subscriptionInfo - Information about the subscription including contractual and other discounts.
 * @returns {number|null} The calculated discount amount or null if no discount is applicable.
 */
export const getSubscriptionDiscount = (subscriptionInfo) => {
  if (!subscriptionInfo) {
    return null
  }

  let discount = null
  if (subscriptionInfo.contractualDiscount?.amount > 0 && !subscriptionInfo.discount?.type) {
    discount = subscriptionInfo.contractualDiscount.amount
  } else if (subscriptionInfo.discount?.type && subscriptionInfo.totalDiscount > 0) {
    discount = subscriptionInfo.totalDiscount
  }

  return discount
}

/**
 * Calculates the final price from a subscription with possible discounts.
 * @param {Object} subscriptionInfo - Information about the subscription including subtotal, discounts, etc.
 * @returns {number, null} The calculated price after applying the discount or 0 if no subscription info is provided.
 */
export const getSubscriptionPriceWithDiscount = (subscriptionInfo) => {
  if (!subscriptionInfo) {
    return null
  }

  let price = subscriptionInfo.subTotal
  if (subscriptionInfo.discount?.type && subscriptionInfo.totalDiscount > 0) {
    price -= subscriptionInfo.totalDiscount
  } else if (subscriptionInfo.contractualDiscount?.amount > 0 && !subscriptionInfo.discount?.type) {
    price -= subscriptionInfo.contractualDiscount.amount
  }

  return price
}

/**
 * Retrieves the applicable discount for a product based on subscription and contract info.
 * @param {Object} subscriptionInfo - Information about the subscription including discounts.
 * @param {Object} contractInfo - Contract information which may include product commitments and discounts.
 * @param {number} salesPrice - The base sales price of the product.
 * @param {string} productId - The ID of the product to find the discount for.
 * @returns {number|null} The calculated discount, or null if no discount is applicable.
 */
export const getProductDiscount = (subscriptionInfo, contractInfo, salesPrice, productId) => {
  let contract
  if (subscriptionInfo) {
    contract = subscriptionInfo.contractInfo
  } else if (contractInfo) {
    contract = contractInfo
  } else {
    return null
  }

  const findDiscountFromContract = contract.productCommitments?.find(
    (commitment) => commitment.product === productId && commitment.discount?.type === 'percentage' && commitment.discount?.amount > 0,
  )

  let discount = null
  if (findDiscountFromContract && !subscriptionInfo?.discount?.type) {
    discount = new Decimal(salesPrice).div(100).times(findDiscountFromContract.discount.amount).toDecimalPlaces(2).toNumber()
  } else if (subscriptionInfo?.discount?.type && subscriptionInfo?.discount?.amount > 0) {
    if (subscriptionInfo.discount.type === 'fixed') {
      discount = subscriptionInfo.discount.amount
    } else if (subscriptionInfo.discount.type === 'percentage') {
      discount = new Decimal(salesPrice).div(100).times(subscriptionInfo.discount.amount).toDecimalPlaces(2).toNumber()
    }
  }

  return discount
}

/**
 * Retrieves the sales price of a product based on the given periodicity, period, and start date.
 * @param {Array<Object>} salesPrices - List of sales prices for the product, including start dates, periodicity, and periods.
 * @param {string} periodic - The type of periodicity (e.g., 'year', 'month') to match.
 * @param {number} period - The number of periods (e.g., 1 year, 1 month) to match.
 * @returns {number, null} The sales price of the product that matches the periodic and period, or 0 if not found.
 */
export const getProductSalesPrice = (salesPrices, periodic, period) => {
  return (
    salesPrices?.find((price) => price.periodic === periodic && price.period === period && DateTime.fromISO(price.startDate) <= DateTime.now())?.price ?? null
  )
}

/**
 * Calculates the sales price of a product after applying any applicable discount.
 * @param {Object} subscriptionInfo - Information about the subscription including discounts.
 * @param {Object} contractInfo - Contract information which may include product commitments and discounts.
 * @param {number} salesPrice - The base sales price of the product.
 * @param {string} productId - The ID of the product to find the discount for.
 * @returns {number} The final sales price after applying the discount, or the base sales price if no discount applies.
 */
export const getProductSalesPriceWithDiscount = (subscriptionInfo, contractInfo, salesPrice, productId) => {
  return getProductDiscount(subscriptionInfo, contractInfo, salesPrice, productId)
    ? new Decimal(salesPrice).minus(new Decimal(getProductDiscount(subscriptionInfo, contractInfo, salesPrice, productId))).toNumber()
    : salesPrice
}

/**
 * Calculates the open payment for a subscription after applying discounts and refund calculations.
 * @param {Object} subscriptionInfo - Information about the subscription including invoiced dates and periodicity.
 * @param {Object} contractInfo - Contract information which may include product commitments and discounts.
 * @param {number} salesPrice - The base sales price of the product.
 * @param {string} productId - The ID of the product to find the discount for.
 * @returns {number} The final amount of open payment after applying refunds and discounts.
 */
export const getOpenPaymentWithDiscount = (subscriptionInfo, contractInfo, salesPrice, productId) => {
  // Determine the amount of days in the subscription
  let daysInSubscription = new Decimal(1)
  if (subscriptionInfo.periodic === 'month') {
    daysInSubscription = new Decimal(365).div(12)
  } else if (subscriptionInfo.periodic === 'year') {
    daysInSubscription = new Decimal(365)
  }

  // And multiply by the period
  daysInSubscription = daysInSubscription.times(subscriptionInfo.period)

  // Calculate the amount of days until which the product is already invoiced
  const daysToNextInvoice = DateTime.fromISO(subscriptionInfo.invoicedUntilDate).diff(DateTime.now().startOf('day'), ['days']).days

  // And make it a percentage of the entire subscription duration
  const subscriptionPeriodPercentage = new Decimal(daysToNextInvoice).div(daysInSubscription)

  // Calculate the refund, based on the duration percentage
  const refund = subscriptionPeriodPercentage.times(new Decimal(getSubscriptionPriceWithDiscount(subscriptionInfo)))

  // Calculate the new price, based on the duration percentage
  const newPrice = subscriptionPeriodPercentage.times(getProductSalesPriceWithDiscount(subscriptionInfo, contractInfo, salesPrice, productId))

  // The final price is the diff between new and old
  return newPrice.minus(refund).toDecimalPlaces(2).toNumber()
}

export const downloadExport = async (service, data) => {
  const response = await service.export(data)
  // The response is a Blob (e.g., Excel file)
  const blob = await response.blob()

  // Extract filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition')

  // Set default filename
  let filename = 'Export.xlsx'

  // Attempt to extract file name from headers
  if (disposition && disposition.includes('attachment')) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '')
    }
  }

  // Offer the Blob as a downloadable file
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export const generateExport = async (service, query = null) => {
  await downloadExport(service, {
    query: query || {},
  }).catch((error) => {
    onError(error, 'customer-contacts')
  })
}

export const serializeRequestBody = (data, schema) => {
  let obj = {}
  const keysInSchema = Object.keys(schema)
  for (const key of keysInSchema) {
    // If the data does not contain the key in the schema, set it to null
    if (typeof data?.[key] === 'undefined') {
      obj[key] = null
      continue
    }

    const value = data[key]

    if (value === '__null__') {
      obj[key] = null
      continue
    }

    if (schema[key]?.type === 'object') {
      const childObject = serializeRequestBody(data[key], schema[key].value)
      const isEmpty = Object.values(childObject).every((x) => x === null || x === '' || x === '__null__')
      if (isEmpty) {
        obj[key] = null
      } else {
        obj[key] = childObject
      }
    } else if (schema[key]?.type === 'array-objects') {
      obj[key] = []
      if (data[key]) {
        for (const entry of data[key]) {
          const childObject = serializeRequestBody(entry, schema[key].value)
          const isEmpty = Object.values(childObject).every((x) => x === null || x === '' || x === '__null__')

          if (isEmpty) {
            obj[key] = schema[key].type === 'boolean' || schema[key].type === 'boolean-small' ? value : null
          } else {
            obj[key].push(childObject)
          }
        }
      }
    } else if (schema[key]?.type === 'number' || schema[key]?.type === 'custom-number') {
      obj[key] = value === null ? null : Number(value)
    } else if (schema[key]?.type === 'boolean' || schema[key]?.type === 'boolean-small') {
      obj[key] = value
    } else {
      obj[key] = value === 'undefined' || value === '' ? null : value
    }
  }
  return obj
}

export const addClickPreventToLinks = (htmlString) => {
  return htmlString.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
    if (/onclick\s*=\s*["'][^"']*["']/.test(attributes)) {
      return `<a ${attributes}>`
    }
    return `<a ${attributes} onclick="event.preventDefault()">`
  })
}

export const setReadOnly = (schema) => {
  for (const field of Object.keys(schema)) {
    if (typeof schema[field].value === 'object' && schema[field].value !== null && !Array.isArray(schema[field].value)) {
      setReadOnly(schema[field].value)
    }
    schema[field].readonly = true
  }
}

export const getImageMeta = (url, cb) => {
  const img = new Image()
  img.onload = () => cb(null, img)
  img.onerror = (err) => cb(err)
  img.src = url
}

export const downloadBlob = async (accessToken, href, filename, download = false) => {
  const response = await fetch(href, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`)
      }
      return response
    })
    .catch((err) => {
      throw err
    })

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)

  if (download) {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)

    // Notify success in your existing UI framework
    Notify.create({
      type: 'positive',
      message: i18n.global.t('Shared.Globals.Download success'),
      progress: true,
      html: true,
    })
  } else {
    // Return the blob URL if user wants to open or handle it in some other way
    return url
  }
}

export const setStockDescription = (line, items, locale) => {
  const translate = (key, values = {}, options = {}) => i18n.global.t(`Admin.Logistics.${key}`, values, { locale, ...options })
  const description = items
    .filter((item) => item.serialNumber)
    .map((item) => {
      const serial = `${translate('Serial number')}: ${item.serialNumber}`
      if (!item.customerWarranty) return serial
      const { startDate, durationMonths } = item.customerWarranty
      const start = DateTime.fromISO(startDate, { zone: 'utc' }).setLocale(locale)
      const duration =
        durationMonths == null
          ? translate('Unknown warranty duration')
          : durationMonths === 0
            ? translate('No warranty')
            : translate('Warranty duration', { count: durationMonths }, { plural: durationMonths })
      const dates = `${start.toLocaleString()}${durationMonths ? ` ${translate('Warranty through')} ${start.plus({ months: durationMonths }).toLocaleString()}` : ''}`
      return `${serial}\n${translate('Customer warranty')}: ${duration} (${dates})`
    })
    .join('\n')
  line.description =
    line.stockDescription && line.description.includes(line.stockDescription)
      ? line.description.replace(line.stockDescription, description)
      : [line.description, description].filter(Boolean).join('\n')
  line.stockDescription = description
  return line
}
